/**
 * Every screen → its design file → its route → its work package.
 *
 * Transcribed from docs/11-screen-map.md, which is also the sync map: if a design
 * file changes, the screens listed against it are the ones to rebuild.
 *
 * This manifest is the contract between three things that otherwise drift apart —
 * the router, the visual-regression harness (which needs to know which built route
 * corresponds to which design frame), and the coverage report. A route that exists
 * in the router but not here has no fidelity gate on it.
 *
 * `status` is honest bookkeeping. `pending` means the route is not built yet, and
 * the harness reports it as PENDING rather than passing it. docs/10 § rules: a
 * placeholder screen is worse than an absent one, because it looks finished in review.
 *
 * ── WHY THERE IS A FOURTH STATUS, AND WHY THE COUNT WENT DOWN ────────────────
 *
 * This file reported 73 of 73 BUILT, and that was true of the pixels and of nothing
 * else. `built` was being used to mean "the route resolves and the frame matches at 2%",
 * which is what the visual gate measures — so a screen whose every row is a constant in
 * the client bundle scored exactly like one reading a live database. A number that counts
 * those two the same is not a coverage number, it is a screenshot count, and 73/73 is
 * precisely the shape of report docs/10 § rules warns about: it looks finished in review.
 *
 * `unwired` is the missing distinction. It says: THE PIXELS ARE RIGHT AND THE FACTS ARE
 * INVENTED. The screen renders, it matches its frame, and what a person reads on it was
 * typed into this repository rather than answered by the server — so a customer looking at
 * it is being shown something that is not about them. It is not a lesser kind of `built`
 * and `coverage()` does not add the two together.
 *
 * THE LINE, stated once so it is applied the same way twice: a screen is `unwired` when a
 * value a READER TAKES AS A FACT ABOUT THE WORLD — a name, a row, a number, an agent's
 * permission level, the terms of a refund — comes from a module in `apps/web/src` instead
 * of from an API response. Drawn CHROME is not a fact: headings, field labels, picker
 * options, placeholder text and a screen's own title are the design's words and belong in
 * the bundle. A5 and A7 hold no facts at all, so they are `built`: a form is finished when
 * it submits, and A5 now does.
 */

export type ScreenStatus = 'pending' | 'built' | 'unwired' | 'deferred';

export interface ScreenEntry {
  /** The design-file frame id, e.g. A1, FM3, LV2. */
  id: string;
  /** The Bangla screen name, exactly as docs/11 gives it. */
  label_bn: string;
  /** Router path. `null` where the design is an in-context sheet, not a route. */
  route: string | null;
  /** File under design/ that is normative for this screen. */
  design: string;
  /** Work package that owns it (docs/10). */
  wp: string;
  /** Declared frame width in the design file. */
  width: 360 | 1180 | 1440;
  status: ScreenStatus;
  note?: string;
  /**
   * Required when `status` is `'unwired'`, and required for the same reason
   * `deferred_reason` is: a status nobody can set without writing down which values are
   * invented and which route would end it is a status that stays rare. Name the SOURCE —
   * what the screen reads today and what it should read instead.
   */
  unwired_reason?: string;
  /**
   * Required when `status` is `'deferred'`, and the requirement is the point.
   *
   * Deferred means the SPECIFICATION contradicts itself for this screen — not that the
   * screen is hard, or late, or nearly right. The visual harness prints this string on
   * every run and does not count the row as a pass. A status nobody can set without
   * writing down why is a status that stays rare, which is the only thing keeping it
   * from becoming a way of retiring inconvenient rows.
   */
  deferred_reason?: string;
}

const P0_SCREENS = 'Oprappo P0 Screens.dc.html';
const P1_FOUNDER = 'Oprappo P1 Founder Mode.dc.html';
const P1_VENDOR = 'Oprappo P1 Vendor.dc.html';
const P2_LEGAL_FINANCE = 'Oprappo P2 Legal and Finance.dc.html';
const P2_HR_CORPORATE = 'Oprappo P2 HR and Corporate.dc.html';
const P2_PROJECTS_EXPLORE = 'Oprappo P2 Projects and Explore.dc.html';
const P0_STATES = 'Oprappo P0 States.dc.html';

export const SCREENS: readonly ScreenEntry[] = [
  // Flow A · Onboarding — WP-C
  { id: 'A1', label_bn: 'ভাষা নির্বাচন', route: '/welcome/language', design: P0_SCREENS, wp: 'WP-C', width: 360, status: 'built' },
  { id: 'A2', label_bn: 'স্বাগত', route: '/welcome', design: P0_SCREENS, wp: 'WP-C', width: 360, status: 'built' },
  { id: 'A3', label_bn: 'ফোন নম্বর', route: '/auth/phone', design: P0_SCREENS, wp: 'WP-C', width: 360, status: 'built' },
  { id: 'A4', label_bn: 'কোড যাচাই', route: '/auth/code', design: P0_SCREENS, wp: 'WP-C', width: 360, status: 'built' },
  { id: 'A5', label_bn: 'প্রোফাইল', route: '/onboarding/profile', design: P0_SCREENS, wp: 'WP-C', width: 360, status: 'built' },
  { id: 'A6', label_bn: 'অনুমতি, প্রসঙ্গে', route: null, design: P0_SCREENS, wp: 'WP-C', width: 360, status: 'built', note: 'in-context sheet, not a route — permission is asked at the moment of need, not during onboarding' },
  { id: 'A7', label_bn: 'লগইন ও পুনরুদ্ধার', route: '/auth/login', design: P0_SCREENS, wp: 'WP-C', width: 360, status: 'built' },

  // Flow B · Discovery — WP-C
  { id: 'B1', label_bn: 'হোম', route: '/', design: P0_SCREENS, wp: 'WP-C', width: 360, status: 'built' },
  { id: 'B2', label_bn: 'সার্চ, খালি', route: '/search', design: P0_SCREENS, wp: 'WP-C', width: 360, status: 'built' },
  { id: 'B3', label_bn: 'সার্চ ফলাফল', route: '/search?q=এসি মেরামত', design: P0_SCREENS, wp: 'WP-C', width: 360, status: 'built', note: 'the drawn query is এসি মেরামত — the B3 frame renders it in the field; an empty q renders B2 instead' },
  { id: 'B4', label_bn: 'ভয়েস', route: '/search/voice', design: P0_SCREENS, wp: 'WP-C', width: 360, status: 'built' },
  { id: 'B5', label_bn: 'এক্সপ্লোর', route: '/explore', design: P0_SCREENS, wp: 'WP-C', width: 360, status: 'built' },
  { id: 'B6', label_bn: 'ক্ষেত্র বিস্তার', route: '/explore/:domain', design: P0_SCREENS, wp: 'WP-C', width: 360, status: 'built' },
  { id: 'B7', label_bn: 'ক্যাটাগরি তালিকা', route: '/explore/:domain/:category', design: P0_SCREENS, wp: 'WP-C', width: 360, status: 'built' },

  // Flow C · Oprappo AI — WP-C
  // Audit finding 01 changed this tab's character: it is no longer the door for a new
  // request (search is the one door), it is where ongoing and past requests live.
  { id: 'C1', label_bn: 'আপনার অনুরোধগুলো', route: '/ai', design: P0_SCREENS, wp: 'WP-C', width: 360, status: 'built' },
  { id: 'C2', label_bn: 'কথোপকথন', route: '/ai/:conversationId', design: P0_SCREENS, wp: 'WP-C', width: 360, status: 'built' },
  { id: 'C3', label_bn: 'তুলনা', route: '/ai/:id/compare', design: P0_SCREENS, wp: 'WP-C', width: 360, status: 'built' },
  { id: 'C4', label_bn: 'পরিকল্পনা', route: '/ai/:id/plan', design: P0_SCREENS, wp: 'WP-C', width: 360, status: 'built' },
  { id: 'C5', label_bn: 'বাজেট অনুমান', route: '/ai/:id/budget', design: P0_SCREENS, wp: 'WP-C', width: 360, status: 'built' },

  // Flow D · Transaction — WP-D
  { id: 'D1', label_bn: 'অফারিং বিস্তার', route: '/offering/:id', design: P0_SCREENS, wp: 'WP-D', width: 360, status: 'built' },
  { id: 'D2', label_bn: 'প্রদানকারী তুলনা', route: '/offering/:id/compare', design: P0_SCREENS, wp: 'WP-D', width: 360, status: 'built' },
  { id: 'D3', label_bn: 'কার্ট', route: '/cart', design: P0_SCREENS, wp: 'WP-D', width: 360, status: 'built', note: 'says "পণ্যের দাম", never "মোট" — delivery and fees are not added yet, and "মোট" there is the first broken promise' },
  { id: 'D4', label_bn: 'চেকআউট', route: '/checkout', design: P0_SCREENS, wp: 'WP-D', width: 360, status: 'built', note: 'renders exactly the fee rows the server returns and computes nothing' },
  { id: 'D5', label_bn: 'পেমেন্ট মেথড', route: '/checkout/payment', design: P0_SCREENS, wp: 'WP-D', width: 360, status: 'built' },
  { id: 'D6', label_bn: 'নিশ্চিতকরণ', route: '/checkout/done', design: P0_SCREENS, wp: 'WP-D', width: 360, status: 'built' },
  { id: 'D7', label_bn: 'রসিদ', route: '/receipt/:ref', design: P0_SCREENS, wp: 'WP-D', width: 360, status: 'built' },

  // Flow E · Activity — WP-D
  { id: 'E1', label_bn: 'অ্যাক্টিভিটি', route: '/activity', design: P0_SCREENS, wp: 'WP-D', width: 360, status: 'built' },
  { id: 'E2', label_bn: 'অ্যাক্টিভিটি বিস্তার', route: '/activity/:id', design: P0_SCREENS, wp: 'WP-D', width: 360, status: 'built', note: 'unknown parts cost reads "দেখে জানাবেন", never ৳ 0 — showing an unknown cost as zero is the easiest way to lie' },
  { id: 'E3', label_bn: 'চ্যাট', route: '/activity/:id/chat', design: P0_SCREENS, wp: 'WP-D', width: 360, status: 'built' },
  { id: 'E4', label_bn: 'সাপোর্ট', route: '/activity/:id/support', design: P0_SCREENS, wp: 'WP-D', width: 360, status: 'built' },

  // Flow F · Account — WP-D
  { id: 'F1', label_bn: 'অ্যাকাউন্ট', route: '/account', design: P0_SCREENS, wp: 'WP-D', width: 360, status: 'built' },
  { id: 'F2', label_bn: 'প্রোফাইল', route: '/account/profile', design: P0_SCREENS, wp: 'WP-D', width: 360, status: 'built' },
  { id: 'F3', label_bn: 'ঠিকানা', route: '/account/addresses', design: P0_SCREENS, wp: 'WP-D', width: 360, status: 'built' },
  { id: 'F4', label_bn: 'পেমেন্ট', route: '/account/payment', design: P0_SCREENS, wp: 'WP-D', width: 360, status: 'built', note: '"হাতে হাতে দেব" is listed as a payment method, not a fallback' },
  { id: 'F5', label_bn: 'ভাষা ও মোড', route: '/account/language', design: P0_SCREENS, wp: 'WP-D', width: 360, status: 'built' },
  { id: 'F6', label_bn: 'নিরাপত্তা', route: '/account/security', design: P0_SCREENS, wp: 'WP-D', width: 360, status: 'built' },

  // Flow G · Internal fulfilment — WP-H, desktop 1180
  { id: 'G1', label_bn: 'কিউ', route: '/ops/queue', design: P0_SCREENS, wp: 'WP-H', width: 1180, status: 'built', note: 'queue is ordered by how late a row is, not by arrival time' },
  { id: 'G2', label_bn: 'ফুলফিলমেন্ট', route: '/ops/fulfilment', design: P0_SCREENS, wp: 'WP-H', width: 1180, status: 'built', note: 'AI and human steps are separate dot colours on the timeline; nothing can be marked done without evidence' },

  // Flow H · Post-audit additions — WP-D
  { id: 'H1', label_bn: 'যাচাইয়ের বিস্তার', route: '/provider/:id/verification', design: P0_SCREENS, wp: 'WP-D', width: 360, status: 'built', note: 'audit finding 04 — renders both arrays; listing the gaps is what makes the badge trustworthy' },
  {
    id: 'H2',
    label_bn: 'ফেরতের শর্ত',
    route: '/policy/refund/:key',
    design: P0_SCREENS,
    wp: 'WP-D',
    width: 360,
    status: 'built',
    note:
      'audit finding 11 — served by GET /v1/policy/refund/:key from the refund_policies row ' +
      'the quote links to (W2-10); the third condition is authored, deviated in ' +
      'docs/deviations/W2-10.md',
  },
  { id: 'H3', label_bn: 'প্রস্তুত ঝুড়ি', route: '/explore/2/grocery', design: P0_SCREENS, wp: 'WP-D', width: 360, status: 'built', note: 'audit finding 09' },

  /*
   * States S1–S11 — WP-K — `Oprappo P0 States.dc.html`.
   *
   * These are not extra screens and they do not claim extra router paths. Each frame is
   * a P0 screen drawn in one state, so its `route` is that screen's own route plus the
   * `?state=` seam `SurfaceStates` reads (`states/boundary.tsx`). `pendingRoutes()`
   * strips the query and finds the path already claimed by the screen's own entry, which
   * is the point: nothing new is mounted, and no harness route exists to keep out of a
   * production build.
   *
   * The `Surface` column of docs/11 § States is load-bearing and is repeated in each
   * note: `?state=` resolves to NOTHING on a surface whose matrix cell is tinted, so a
   * row moved to the wrong surface stops capturing the state instead of failing loudly.
   *
   * Two of the eleven matrix rows — সেবা সাময়িক বন্ধ and অনুমোদন অপেক্ষমাণ — have no
   * entry here and cannot have one. They are drawn in no design file, so there is no
   * frame to gate them against; see states/INTEGRATION.md § 4.
   */
  { id: 'S1', label_bn: 'নতুন ব্যবহারকারীর হোম', route: '/?state=new_user', design: P0_STATES, wp: 'WP-K', width: 360, status: 'built', note: 'discovery · B1 হোম, empty' },
  { id: 'S2', label_bn: 'কোনো অর্ডার নেই', route: '/activity?state=new_user', design: P0_STATES, wp: 'WP-K', width: 360, status: 'built', note: 'activity · E1, empty' },
  { id: 'S3', label_bn: 'লোডিং — স্কেলিটন', route: '/search?q=এসি মেরামত&state=loading', design: P0_STATES, wp: 'WP-K', width: 360, status: 'built', note: 'discovery · B3; the drawn query stays in the field, so the route carries it' },
  { id: 'S4', label_bn: 'কোনো এআই ইনসাইট নেই', route: '/ai/:conversationId?state=new_user', design: P0_STATES, wp: 'WP-K', width: 360, status: 'built', note: 'ai · C2 কথোপকথন; also the AI presentation of সেবাটি আসছে — see NewUserState' },
  { id: 'S5', label_bn: 'পেমেন্ট ব্যর্থ', route: '/checkout/payment?state=payment_failed', design: P0_STATES, wp: 'WP-K', width: 360, status: 'built', note: 'transaction · D5' },
  { id: 'S6', label_bn: 'ইন্টারনেট নেই', route: '/?state=offline', design: P0_STATES, wp: 'WP-K', width: 360, status: 'built', note: 'discovery · B1 হোম' },
  { id: 'S7', label_bn: 'সিঙ্ক ত্রুটি', route: '/activity?state=sync_error', design: P0_STATES, wp: 'WP-K', width: 360, status: 'built', note: 'activity · E1; one stale label per row, never one banner' },
  { id: 'S8', label_bn: 'প্রদানকারী অনুপলব্ধ', route: '/offering/:id?state=provider_unavailable', design: P0_STATES, wp: 'WP-K', width: 360, status: 'built', note: 'transaction · D1' },
  { id: 'S9', label_bn: 'যাচাই অপেক্ষমাণ', route: '/account/payment?state=verification_pending', design: P0_STATES, wp: 'WP-K', width: 360, status: 'built', note: 'account · F4, the screen that owns অপ্রাপ্য ক্যাশ — which is what the verification blocks' },
  { id: 'S10', label_bn: 'অনুমতি নেই', route: '/account?state=permission_denied', design: P0_STATES, wp: 'WP-K', width: 360, status: 'built', note: 'account · F1; drawn on founder mode’s ground, and INTEGRATION.md § 3 files founder under the account column' },
  { id: 'S11', label_bn: 'সেবাটি আসছে', route: '/search?q=ডাক্তার দেখাতে চাই&state=domain_not_live', design: P0_STATES, wp: 'WP-K', width: 360, status: 'built', note: 'discovery · B3; the drawn query is ডাক্তার দেখাতে চাই' },

  // P1 · Founder Mode — WP-G
  { id: 'FM1', label_bn: 'মোড প্রবেশ', route: '/founder/enter', design: P1_FOUNDER, wp: 'WP-G', width: 1440, status: 'built' },
  { id: 'FM2', label_bn: 'আজকের ব্রিফ', route: '/founder', design: P1_FOUNDER, wp: 'WP-G', width: 1440, status: 'built', note: 'exactly three items, enforced server-side' },
  { id: 'FM3', label_bn: 'অনুমোদন কেন্দ্র', route: '/founder/approvals', design: P1_FOUNDER, wp: 'WP-G', width: 1440, status: 'built' },
  { id: 'FM4', label_bn: 'ইন্টেলিজেন্স সেন্টার', route: '/founder/intelligence', design: P1_FOUNDER, wp: 'WP-G', width: 1440, status: 'built' },
  {
    id: 'FM5',
    label_bn: 'এআই টিম ও প্রোফাইল',
    route: '/founder/ai-team',
    design: P1_FOUNDER,
    wp: 'WP-G',
    width: 1440,
    status: 'built',
    note: 'W2-11 — roster and profile read GET /v1/founder/ai-team and /ai-team/:key; level and stop are audited founder-mode writes',
  },
  { id: 'FM6', label_bn: 'ব্রিফ — মোবাইল', route: '/founder', design: P1_FOUNDER, wp: 'WP-G', width: 360, status: 'built', note: 'cap table and share transactions are absent at 360 — not shrunk, not masked' },

  // P1 · Vendor — WP-H
  { id: 'V1', label_bn: 'ধরন বাছাই', route: '/vendor/apply', design: P1_VENDOR, wp: 'WP-H', width: 360, status: 'built' },
  { id: 'V2', label_bn: 'যাচাই অপেক্ষমাণ', route: '/vendor/verify', design: P1_VENDOR, wp: 'WP-H', width: 360, status: 'built' },
  { id: 'V3', label_bn: 'ড্যাশবোর্ড', route: '/vendor', design: P1_VENDOR, wp: 'WP-H', width: 360, status: 'built' },
  { id: 'V4', label_bn: 'সেবা যোগ করা', route: '/vendor/listings/new', design: P1_VENDOR, wp: 'WP-H', width: 360, status: 'built' },
  { id: 'V5', label_bn: 'পেআউট ও কমিশন', route: '/vendor/payouts', design: P1_VENDOR, wp: 'WP-H', width: 1180, status: 'built', note: 'commission shown per transaction' },

  // P2 · drawn layouts
  { id: 'LV1', label_bn: 'ভল্ট সূচি', route: '/legal', design: P2_LEGAL_FINANCE, wp: 'WP-N', width: 1440, status: 'built', note: 'ordered by expiry, not by class — a date list is what stops a licence being lost' },
  { id: 'LV2', label_bn: 'নথির বিস্তার', route: '/legal/:id', design: P2_LEGAL_FINANCE, wp: 'WP-N', width: 1440, status: 'built' },
  { id: 'FN1', label_bn: 'অর্থ ড্যাশবোর্ড', route: '/finance', design: P2_LEGAL_FINANCE, wp: 'WP-P2', width: 1440, status: 'built' },
  { id: 'FN2', label_bn: 'সাধারণ লেজার', route: '/finance/ledger', design: P2_LEGAL_FINANCE, wp: 'WP-P2', width: 1440, status: 'built', note: 'two-name actor column — "এআই → সালমা"; a DB trigger forbids a row credited to AI alone' },
  { id: 'HR1', label_bn: 'কর্মী ডিরেক্টরি', route: '/hr', design: P2_HR_CORPORATE, wp: 'WP-P2', width: 1440, status: 'built' },
  { id: 'HR2', label_bn: 'কর্মীর প্রোফাইল', route: '/hr/:id', design: P2_HR_CORPORATE, wp: 'WP-P2', width: 1440, status: 'built' },
  { id: 'CP1', label_bn: 'ক্যাপ টেবিল', route: '/corporate/cap-table', design: P2_HR_CORPORATE, wp: 'WP-P2', width: 1440, status: 'built', note: 'desktop only; ownership is derived from share_transactions, never stored' },
  {
    id: 'EX1',
    label_bn: 'এক্সপ্লোর v2',
    route: '/explore',
    design: P2_PROJECTS_EXPLORE,
    wp: 'WP-P2',
    width: 360,
    status: 'deferred',
    note: '12 live domains, three groups, no subtitles — the group names do the work at twelve',
    // B5 (Oprappo P0 Screens, five domains) and EX1 (this, twelve) both map to /explore.
    // One route renders one screen, so whichever is built the other reports a diff — and
    // that diff measures the specification disagreeing with itself, not the build drifting
    // from the design, which is the only thing the gate is for.
    //
    // B5 wins for launch, and not by preference: CLAUDE.md § launch scope names five life
    // domains, which is what B5 draws. EX1 is the twelve-domain screen for after those
    // twelve exist, and it will be correct then rather than now.
    deferred_reason:
      'B5 and EX1 both claim /explore and draw different screens. CLAUDE.md § launch scope ' +
      'fixes P0 at five life domains, which is B5, so /explore renders B5 and EX1 is the ' +
      'P2 screen for when twelve domains are live. Resolve by giving EX1 its own route in ' +
      'docs/11, or by superseding B5 when the twelfth domain launches.',
  },
  { id: 'NT1', label_bn: 'নোটিফিকেশন কেন্দ্র', route: '/notifications', design: P2_PROJECTS_EXPLORE, wp: 'WP-M', width: 360, status: 'built' },
  { id: 'NT2', label_bn: 'খবরের পছন্দ', route: '/account/notifications', design: P2_PROJECTS_EXPLORE, wp: 'WP-M', width: 360, status: 'built' },
  { id: 'PR1', label_bn: 'প্রজেক্ট কানবান', route: '/projects/:id', design: P2_PROJECTS_EXPLORE, wp: 'WP-P2', width: 1440, status: 'built' },
];

/** Routes only — entries whose design is an in-context sheet are excluded. */
export const ROUTABLE = SCREENS.filter((s) => s.route !== null);

export function screensFor(wp: string): ScreenEntry[] {
  return SCREENS.filter((s) => s.wp === wp);
}

/**
 * What is actually finished, and what only looks it.
 *
 * `built` counts ONLY `status === 'built'`. `unwired`, `pending` and `deferred` are
 * reported beside it and never folded into it — see the header for why 73/73 was a
 * screenshot count rather than a coverage number. `pending` keeps meaning what it meant,
 * which is why it is computed from its own status and not as a remainder: a remainder
 * quietly absorbs every status added later, and this is the second one to be added.
 */
export function coverage(): {
  total: number;
  built: number;
  unwired: number;
  pending: number;
  deferred: number;
} {
  const count = (status: ScreenStatus): number =>
    SCREENS.filter((s) => s.status === status).length;
  return {
    total: SCREENS.length,
    built: count('built'),
    unwired: count('unwired'),
    pending: count('pending'),
    deferred: count('deferred'),
  };
}

/** The screens whose pixels are the frame's and whose facts are the bundle's. */
export const UNWIRED: readonly ScreenEntry[] = SCREENS.filter((s) => s.status === 'unwired');
